import type {
	AIConversationAction,
	AiGeneratedArtifacts,
	AiGeneratedDocument,
	GeneratedArtifactsQuery,
	GeneratedPodcastQuery,
	GenerateLernsetWorksheet,
	GenerateSuggestions,
	UpdateGeneratedPodcast,
} from '@kimuno/api-sdk';

import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';

const service = {
	generateLernsetWorksheet,
	streamConversationResponse,
	generateAISuggestions,
	uploadAttachment,
	getAttachment,
	getAttachmentsRemainingLimit,
	getGeneratedImagesRemainingLimit,
	getPromptsRemainingLimit,
	getGeneratedDocument,
	getGeneratedArtifacts,
	createPublicDocumentLink,
	getPublicDocument,
	createPublicPodcastLink,
	getPublicPodcast,
	updateGeneratedDocument,
	uploadImage,
	getGeneratedPodcast,
	updateGeneratedPodcast,
	regenerateGeneratedPodcast,
	stopConversationGeneration,
};

function generateLernsetWorksheet(payload: GenerateLernsetWorksheet) {
	notifyBugsnagNetwork('AI', 'generateLernsetWorksheet', { ...payload });
	return handleUnauthorized(() => api.ai.generateLernsetWorksheet(payload));
}

function streamConversationResponse(payload: AIConversationAction, abortSignal?: AbortSignal) {
	notifyBugsnagNetwork('AI', 'streamConversationResponse', { ...payload });
	return handleUnauthorized(() => api.ai.streamAiConversationResponse(payload, abortSignal));
}

function generateAISuggestions(payload: GenerateSuggestions) {
	notifyBugsnagNetwork('AI', 'generateAISuggestions', { ...payload });
	return handleUnauthorized(() => api.ai.generateSuggestions(payload));
}

function uploadAttachment(file: FormData, abortSignal?: AbortSignal) {
	notifyBugsnagNetwork('AI', 'uploadFile', { file });
	return handleUnauthorized(() => api.ai.uploadAttachment(file, abortSignal));
}

function getAttachment(attachmentHashName: string) {
	notifyBugsnagNetwork('AI', 'getAttachment', { attachmentHashName });
	return handleUnauthorized(() => api.ai.getAttachment(attachmentHashName));
}

function getAttachmentsRemainingLimit() {
	notifyBugsnagNetwork('AI', 'getAttachmentsRemainingLimit');
	return handleUnauthorized(() => api.ai.getAttachmentsRemainingLimit());
}

function getGeneratedImagesRemainingLimit() {
	notifyBugsnagNetwork('AI', 'getGeneratedImagesRemainingLimit');
	return handleUnauthorized(() => api.ai.getGeneratedImagesRemainingLimit());
}

function getPromptsRemainingLimit() {
	notifyBugsnagNetwork('AI', 'getPromptsRemainingLimit');
	return handleUnauthorized(() => api.ai.getPromptsRemainingLimit());
}

function getGeneratedDocument(documentId: string) {
	notifyBugsnagNetwork('AI', 'getGeneratedDocument', { documentId });
	return handleUnauthorized(() => api.ai.getGeneratedDocument(documentId));
}

function getGeneratedArtifacts(query?: GeneratedArtifactsQuery): Promise<AiGeneratedArtifacts> {
	notifyBugsnagNetwork('AI', 'getGeneratedArtifacts', { ...query });
	return handleUnauthorized(() => api.ai.getGeneratedArtifacts(query));
}

function createPublicDocumentLink(documentId: string) {
	notifyBugsnagNetwork('AI', 'createPublicDocumentLink', { documentId });
	return handleUnauthorized(() => api.ai.createPublicDocumentLink(documentId));
}

function getPublicDocument(accessToken: string) {
	notifyBugsnagNetwork('AI', 'getPublicDocument', { accessToken });
	return handleUnauthorized(() => api.ai.getPublicDocument(accessToken));
}

function createPublicPodcastLink(podcastId: string) {
	notifyBugsnagNetwork('AI', 'createPublicPodcastLink', { podcastId });
	return handleUnauthorized(() => api.ai.createPublicPodcastLink(podcastId));
}

function getPublicPodcast(accessToken: string) {
	notifyBugsnagNetwork('AI', 'getPublicPodcast', { accessToken });
	return handleUnauthorized(() => api.ai.getPublicPodcast(accessToken));
}

function updateGeneratedDocument(
	documentId: string,
	payload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>>
) {
	notifyBugsnagNetwork('AI', 'updateGeneratedDocument', { documentId, ...payload });
	// Filter out undefined values to match API type requirements
	const filteredPayload: Partial<Pick<AiGeneratedDocument, 'title' | 'content'>> = {};
	if (payload.title !== undefined) {
		filteredPayload.title = payload.title;
	}
	if (payload.content !== undefined) {
		filteredPayload.content = payload.content;
	}
	return handleUnauthorized(() =>
		api.ai.updateGeneratedDocument(
			documentId,
			filteredPayload as Pick<AiGeneratedDocument, 'title' | 'content'>
		)
	);
}

function uploadImage(formData: FormData, abortSignal?: AbortSignal) {
	notifyBugsnagNetwork('AI', 'uploadImage', { formData });
	return handleUnauthorized(() => api.ai.uploadImage(formData, abortSignal));
}

function getGeneratedPodcast(podcastId: string, query?: GeneratedPodcastQuery) {
	notifyBugsnagNetwork('AI', 'getGeneratedPodcast', { podcastId, ...query });
	return handleUnauthorized(() => api.ai.getGeneratedPodcast(podcastId, query));
}

function updateGeneratedPodcast(podcastId: string, payload: UpdateGeneratedPodcast) {
	notifyBugsnagNetwork('AI', 'updateGeneratedPodcast', { podcastId, ...payload });
	return handleUnauthorized(() => api.ai.updateGeneratedPodcast(podcastId, payload));
}

function regenerateGeneratedPodcast(podcastId: string) {
	notifyBugsnagNetwork('AI', 'regenerateGeneratedPodcast', { podcastId });
	return handleUnauthorized(() => api.ai.regenerateGeneratedPodcast(podcastId));
}

function stopConversationGeneration(conversationId: string, podcastId?: string) {
	notifyBugsnagNetwork('AI', 'stopConversationGeneration', { conversationId, podcastId });
	return handleUnauthorized(() => api.ai.stopConversationGeneration(conversationId, podcastId));
}

export default service;
